import { useCallback, useRef, useState } from 'react';

import {
  type CardDisputeEligibilityRequest,
  type CardDisputeEligibilityResponse,
  cardsCheckCardDisputeEligibility,
} from '@column/api';

/**
 * Read-only eligibility checks for the dispute wizard. The backend is a POST but never writes;
 * results are keyed to the complete request that produced them so a stale response can never
 * overwrite the answer for a newer selection. Create revalidates under lock server-side, so a
 * passing check is guidance, not a reservation.
 */
export const useCardDisputeEligibility = () => {
  const [eligibility, setEligibility] = useState<CardDisputeEligibilityResponse | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [isChecking, setIsChecking] = useState(false);
  const [checkedRequest, setCheckedRequest] = useState<CardDisputeEligibilityRequest | undefined>();

  // Newest-request wins: only the request the hook last accepted may commit results.
  const sequenceRef = useRef(0);

  const reset = useCallback(() => {
    sequenceRef.current += 1;
    setIsChecking(false);
    setEligibility(undefined);
    setError(undefined);
    setCheckedRequest(undefined);
  }, []);

  const check = useCallback(
    async (request: CardDisputeEligibilityRequest): Promise<CardDisputeEligibilityResponse | undefined> => {
      const sequence = ++sequenceRef.current;
      setIsChecking(true);
      setError(undefined);

      try {
        const { data } = await cardsCheckCardDisputeEligibility({
          body: request,
          throwOnError: true,
        });
        if (sequence !== sequenceRef.current) {
          return undefined;
        }
        setEligibility(data);
        setCheckedRequest(request);
        return data;
      } catch (err) {
        if (sequence !== sequenceRef.current) {
          return undefined;
        }
        setEligibility(undefined);
        setCheckedRequest(undefined);
        setError(err instanceof Error && err.message ? err.message : 'Failed to check dispute eligibility.');
        return undefined;
      } finally {
        if (sequence === sequenceRef.current) {
          setIsChecking(false);
        }
      }
    },
    []
  );

  return { check, reset, eligibility, checkedRequest, isChecking, error };
};
